import { makeAutoObservable, runInAction } from "mobx";
import { IOrder } from "@/interfaces";
import gql from "@/repo/apolloClient";
import { deleteNullUndefinedFields, showNotification } from "@/utility";
import { ORDER_STATUS, ORDER_TYPE } from "@/@types/EnumType";
import { ICreateOrder } from "@/interfaces";
import { IOrderFeedback } from "@/interfaces/post/order";
import RestAPIClient from "@/repo/restApi";
import Logo from "@/assets/icons/P2P_Logo.svg";

export class OrderStore {
  private myPostsOrders: IOrder[] = [];
  private myOrder?: IOrder[];
  private selectOrder?: IOrder;
  restApi: RestAPIClient<unknown>;
  constructor() {
    makeAutoObservable(this);
    this.restApi = new RestAPIClient("cryptoMarketPlace");
  }
  //#region actions
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private handleMyOrderUpdate = (response: any, user_id: string) => {
    runInAction(() => {
      const newOrders = (response.data.orders as Array<IOrder>) ?? [];
      const latestOrderTimestamp = localStorage.getItem("latestOrderTimestamp");

      const latestTimestamp = latestOrderTimestamp
        ? Number(latestOrderTimestamp)
        : null;
      let notifyCount = 0;
      for (const order of newOrders.filter(
        (order) => order.created_by !== user_id,
      )) {
        const orderTimestamp = new Date(order.created_at).getTime();
        const orderPageURL = "#/trade/orders/" + order.order_id;

        if (notifyCount === 5) break;

        if (!latestTimestamp || orderTimestamp > latestTimestamp) {
          notifyCount += 1;
          showNotification({
            title: "New Order!",
            body: "You have received a new order. Click to view details.",
            icon: Logo,
            path: "/" + orderPageURL,
          });
        }
      }

      this.myOrder = newOrders;

      const latestOrder = newOrders[newOrders.length - 1];
      if (latestOrder) {
        const latestOrderTimestamp = new Date(latestOrder.created_at)
          .getTime()
          .toString();
        localStorage.setItem("latestOrderTimestamp", latestOrderTimestamp);
      }
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private handleSelectedOrderUpdate = (response: any) => {
    runInAction(() => {
      this.selectOrder = response.data.orders[0] ?? null;
    });
  };
  public createOrder = async (request: ICreateOrder) => {
    // delete all null or undefined properties
    const newObj = deleteNullUndefinedFields(request);

    const mutation = `
      mutation createOrder($obj: orders_insert_input!) {
        insert_orders_one(object: $obj) {
          order_id
          tx_hash
        }
      }
    `;
    const result = await gql.Mutation(mutation, { obj: newObj });
    await this.restApi.post({
      method: "updateOrderIdFromTXHash",
      request: { input: { tx_hash: result.data.insert_orders_one.tx_hash } },
    });
    await this.restApi.post({
      method: "notifyNewOrder",
      request: { order_id: result.data.insert_orders_one.order_id },
    });
    return result.data.insert_orders_one.order_id;
  };
  public updateOrderStatus = async (request: {
    status: ORDER_STATUS;
    order_id: string;
    user_id: string;
  }): Promise<string> => {
    const mutation = `
    mutation MyMutation($order_id: uuid!, $status: String) {
      update_orders_by_pk(pk_columns: {order_id: $order_id}, _set: {status: $status}) {
        order_id
      }
    }    
    `;
    const result = await gql.Mutation(mutation, {
      status: request.status,
      order_id: request.order_id,
    });
    return result.data.update_orders_by_pk.order_id;
  };
  public subscribeToOrder = async (order_id: string) => {
    const query = `
    subscription getOrders{
        orders(where: {order_id: { _eq: "${order_id}" }},order_by:{created_at: desc}) {
          asset {
            address
            decimals
            name
            symbol
          }
          buyer_id
          currency
          created_at
          order_amount
          order_type
          tx_hash
          contract_order_id
          payment {
            payment_method {
              payment_name
              status
              payment_id
            }
            payment_attributes {
              attribute_value
              payment_id
              id
              payment_method_attribute {
                attribute_name
                attribute_label
                id
                is_image
              }
            }
          }
          post {
            address
            max_amount
            min_amount
            description
            post_type
            payment_method
            title
          }
          seller_id
          store_id
          order_id
          price
          status
          address
          order_number
          buyer_address
          seller_address
          post_id
          user {
            username
            firstname
            lastname
            phone_number
            wallet_address
          }
          appeals {
            id
            created_by
            appeal_to
          }
        }
      }`;
    const subscription = (await gql.Subscribe(query)).subscribe({
      next: this.handleSelectedOrderUpdate,
      error: (error) => console.error("Error fetching order: ", error),
    });

    return () => {
      subscription.unsubscribe();
      runInAction(() => {
        this.selectOrder = undefined;
      });
    };
  };
  public subscribeToMyOrders = async (user_id: string) => {
    const query = `
    subscription getOrders {
        orders(where: { _or: [{ buyer_id: { _eq: "${user_id}" } }, { seller_id: { _eq: "${user_id}" } }] }, order_by: { created_at: asc }) {
          asset {
            address
            decimals
            name
            symbol
            asset_id
          }
          buyer_id
          currency
          order_amount
          order_type
          payment {
            payment_method {
              payment_name
              status
              payment_id
            }
            payment_attributes {
              attribute_value
              payment_id
              id
              payment_method_attribute {
                attribute_name
                attribute_label
                id
                is_image
              }
            }
          }
          post {
            address
            max_amount
            min_amount
            description
            payment_method
            title
          }
          seller_id
          order_id
          price
          status
          address
          contract_order_id
          order_number
          buyer_address
          seller_address
          post_id
          user {
            username
            firstname
            lastname
            phone_number
          }
          created_at
          created_by
        }
      }`;
    const subscription = (await gql.Subscribe(query)).subscribe({
      next: (response) => this.handleMyOrderUpdate(response, user_id),
      error: (error) => console.error("Error fetching my orders: ", error),
    });

    return () => subscription.unsubscribe();
  };
  public fetchMyPostsOrders = async (user_id: string) => {
    const query = `
      query getMyOrders($user_id: uuid!) {
        orders(where: {store_id: { _eq: $user_id }}) {
          asset {
            address
            decimals
            name
            symbol
          }
          buyer_id
          currency
          order_amount
          order_type
          payment {
            payment_method {
              payment_name
              status
              payment_id
            }
            payment_attributes {
              attribute_value
              payment_id
              id
              payment_method_attribute {
                attribute_name
                attribute_label
                id
                is_image
              }
            }
          }
          post {
            address
            max_amount
            min_amount
            description
            payment_method
            title
          }
          seller_id
          order_id
          price
          status
          address
        }
      }`;
    const result = await gql.Query(query, { user_id });
    runInAction(() => {
      this.myPostsOrders = (result.data.orders as Array<IOrder>) ?? [];
    });
    return this.myPostsOrders;
  };
  public giveOrderFeedback = async (
    feedback: IOrderFeedback,
  ): Promise<void> => {
    const existingFeedback = await this.getOrderFeedback(feedback.order_id);

    if (existingFeedback) {
      const updatedFeedbackQuery = `
        mutation updateOrderFeedback($order_id: String!, $feedback: order_feedbacks_set_input!) {
          update_order_feedbacks(where: { order_id: { _eq: $order_id } }, _set: $feedback) {
            affected_rows
          }
        }
      `;
      const updatedFeedbackVariables = {
        order_id: feedback.order_id,
        feedback: {
          ...(feedback.buyer_comment !== undefined && {
            buyer_comment: feedback.buyer_comment,
          }),
          ...(feedback.seller_comment !== undefined && {
            seller_comment: feedback.seller_comment,
          }),
          ...(feedback.buyer_rating !== undefined && {
            buyer_rating: feedback.buyer_rating,
          }),
          ...(feedback.seller_rating !== undefined && {
            seller_rating: feedback.seller_rating,
          }),
        },
      };
      await gql.Mutation(updatedFeedbackQuery, updatedFeedbackVariables);
    } else {
      const newFeedbackQuery = `
        mutation createOrderFeedback($feedback: order_feedbacks_insert_input!) {
          insert_order_feedbacks_one(object: $feedback) {
            order_id
          }
        }
      `;
      const newFeedbackVariables = {
        feedback: feedback,
      };
      await gql.Mutation(newFeedbackQuery, newFeedbackVariables);
    }
  };

  //#endregion

  //#region getters
  public get selectedOrder() {
    return this.selectOrder;
  }
  public get myOrders() {
    return this.myOrder ?? [];
  }
  public get getMyPostsOrders() {
    return this.myPostsOrders ?? [];
  }

  public getLatestOrders = async (orderType: ORDER_TYPE) => {
    const query = `
      query getLatestOrders($orderType: String!) {
        orders(order_by: {created_at: desc}, limit: 5, where: {order_type: {_eq: $orderType}}) {
          asset
          buyer_id
          created_at
          currency
          deleted_at
          order_amount
          order_id
          order_type
          payment_method
          seller_id
          status
          store_id
          updated_at
          price
        }
      }`;
    const result = await gql.Query(query, { orderType });
    return result.data as IOrder[];
  };
  public async getOrderFeedback(
    order_id: string,
  ): Promise<IOrderFeedback | undefined> {
    const feedbackQuery = `
      query getOrderFeedback($order_id: String!) {
        order_feedbacks(where: { order_id: { _eq: $order_id } }) {
          order_id
          buyer_id
          seller_id
          buyer_comment
          seller_comment
          buyer_rating
          seller_rating
        }
      }
    `;
    const feedbackResult = await gql.Query(feedbackQuery, {
      order_id,
    });
    const feedback = feedbackResult.data.order_feedbacks[0];

    return feedback;
  }
  //#endregion
}
